import { Controller } from "@hotwired/stimulus"

// Networks the contest runs on. `locative` is the Slovak form used in the
// "Zobraziť komentár na …" link.
const PLATFORMS = {
  facebook: {
    label:    "Facebook",
    locative: "Facebooku",
    chip:     "bg-sky-500/15 text-sky-200 ring-1 ring-sky-400/30"
  },
  instagram: {
    label:    "Instagram",
    locative: "Instagrame",
    chip:     "bg-fuchsia-500/15 text-fuchsia-200 ring-1 ring-fuchsia-400/30"
  }
}

const SHORT_HASH_LENGTH = 12

// Picks a random winner from a JSON list of contest comments
// ({ name, post_url, platform, optional username }) with a slot-machine style
// animation.
// Everything runs in the browser — the file never leaves the device.
//
// Transparency aids meant to be visible on the recorded video: a live clock,
// the SHA-256 of the loaded file (so a published copy can be checked against
// it), what the file contained and what was left out, an alphabetically
// numbered list of participants with their network, the per-network breakdown,
// and the drawn number shown next to the winner's name.
//
// The first draw is the winner and cannot be repeated. Further draws pick
// backups from the remaining participants, up to `backups`, and every result
// stays on screen with its number and time.
export default class extends Controller {
  static targets = [
    "clock", "picker", "error", "stage", "number", "name", "platform", "label", "drawnAt",
    "count", "breakdown", "stats", "hash", "button", "commentLink", "results", "search", "list", "confetti"
  ]

  static values = {
    duration: { type: Number, default: 6000 },
    backups:  { type: Number, default: 2 }
  }

  connect() {
    this.results = []
    this.updateClock()
    this.clockTimer = setInterval(() => this.updateClock(), 250)
  }

  disconnect() {
    clearInterval(this.clockTimer)
  }

  updateClock() {
    const now = new Date()
    this.clockTarget.textContent = `${this.formatDate(now)} · ${this.formatTime(now)}`
  }

  async load(event) {
    const file = event.target.files[0]
    if (!file) return

    // Hash the raw bytes, so the digest matches `shasum -a 256` of the file as
    // published — not a re-serialised or normalised copy.
    const bytes = await file.arrayBuffer()
    const text = new TextDecoder().decode(bytes)

    try {
      this.participants = this.parse(text)
    } catch {
      this.showError("Súbor sa nepodarilo prečítať — očakáva sa JSON pole objektov s name, post_url a platform.")
      return
    }

    if (this.participants.length < 2) {
      this.showError("Súbor musí obsahovať aspoň dvoch účastníkov s platným odkazom na komentár.")
      return
    }

    this.fileHash = await this.sha256(bytes)
    this.hashExpanded = false

    this.countTarget.textContent = this.participants.length
    this.breakdownTarget.textContent = this.breakdown()
    this.statsTarget.textContent = this.statsCaption()
    this.renderHash()
    this.renderList()
    this.pickerTarget.classList.add("hidden")
    this.stageTarget.classList.remove("hidden")
  }

  // Per the contest rules: repeated comments under the same post do not add
  // chances, so the same person on the same network keeps only its first
  // comment. A person is identified by `username` when the export carries it
  // (display names on Instagram are not unique), otherwise by name. Facebook
  // and Instagram count separately — commenting on both networks means two
  // entries. The result is sorted alphabetically and numbered from 1 so the
  // drawn number can be checked against the list.
  parse(text) {
    const entries = JSON.parse(text)
    if (!Array.isArray(entries)) throw new TypeError("Expected an array")

    this.stats = { total: entries.length, repeated: 0, invalid: 0 }

    const seen = new Map()
    for (const entry of entries) {
      const name     = String(entry?.name ?? "").trim()
      const username = String(entry?.username ?? "").trim().replace(/^@/, "")
      const url      = this.commentUrl(entry?.post_url)
      const platform = String(entry?.platform ?? "").trim().toLowerCase()
      if (!name || !url || !(platform in PLATFORMS)) {
        this.stats.invalid++
        continue
      }

      const key = `${platform} ${(username || name).toLocaleLowerCase("sk")}`
      if (seen.has(key)) {
        this.stats.repeated++
      } else {
        seen.set(key, { name, username, url, platform })
      }
    }

    const collator = new Intl.Collator("sk", { sensitivity: "base" })
    return [...seen.values()]
      .sort((a, b) =>
        collator.compare(a.name, b.name) ||
        collator.compare(a.username, b.username) ||
        collator.compare(a.platform, b.platform)
      )
      .map((participant, index) => ({ ...participant, number: index + 1 }))
  }

  // The comment link ends up in an href, so only plain http(s) links are kept.
  commentUrl(value) {
    try {
      const url = new URL(String(value ?? "").trim())
      return url.protocol === "https:" || url.protocol === "http:" ? url.href : null
    } catch {
      return null
    }
  }

  async sha256(bytes) {
    const digest = await crypto.subtle.digest("SHA-256", bytes)
    return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("")
  }

  breakdown() {
    const counts = new Map()
    for (const participant of this.participants) {
      counts.set(participant.platform, (counts.get(participant.platform) ?? 0) + 1)
    }

    // Fixed network order, so the caption reads the same way every time it is
    // recorded.
    return Object.entries(PLATFORMS)
      .filter(([platform]) => counts.has(platform))
      .map(([platform, info]) => `${info.label} ${counts.get(platform)}`)
      .join(" · ")
  }

  // What the file held versus what made it into the draw, so nobody has to
  // wonder whether entries went missing.
  statsCaption() {
    const { total, repeated, invalid } = this.stats

    return [
      `V súbore ${total} ${this.plural(total, "záznam", "záznamy", "záznamov")}`,
      `${repeated} ${this.plural(repeated, "opakovaný komentár", "opakované komentáre", "opakovaných komentárov")}`,
      `${invalid} bez platnej siete alebo odkazu`
    ].join(" · ")
  }

  plural(count, one, few, many) {
    if (count === 1) return one
    if (count >= 2 && count <= 4) return few
    return many
  }

  renderHash() {
    const shown = this.hashExpanded ? this.fileHash : `${this.fileHash.slice(0, SHORT_HASH_LENGTH)}…`
    this.hashTarget.textContent = `SHA-256 ${shown}`
    this.hashTarget.title = this.fileHash
  }

  toggleHash() {
    this.hashExpanded = !this.hashExpanded
    this.renderHash()
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  renderList() {
    const rows = this.participants.map((participant) => {
      const info = PLATFORMS[participant.platform]

      const row = document.createElement("li")
      row.className = "flex items-baseline gap-4 px-5 py-2"
      row.dataset.number = participant.number
      row.dataset.name = `${participant.name} ${participant.username}`.toLocaleLowerCase("sk")

      const number = document.createElement("span")
      number.className = "w-12 shrink-0 font-mono tabular-nums text-slate-500"
      number.textContent = `${participant.number}.`

      const name = document.createElement("span")
      name.textContent = participant.name

      const platform = document.createElement("span")
      platform.className = `ml-auto shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${info.chip}`
      platform.textContent = info.label

      row.append(number, name, ...this.usernameElements(participant), platform)
      return row
    })

    this.listTarget.replaceChildren(...rows)
  }

  // The handle is shown after the name wherever the export provides it, so two
  // people with the same display name can be told apart on the video.
  usernameElements(participant) {
    if (!participant.username) return []

    const username = document.createElement("span")
    username.className = "text-sm text-slate-500"
    username.textContent = `@${participant.username}`
    return [username]
  }

  filter() {
    const query = this.searchTarget.value.trim().toLocaleLowerCase("sk")

    for (const row of this.listTarget.children) {
      row.classList.toggle("hidden", Boolean(query) && !row.dataset.name.includes(query))
    }
  }

  // Participants not drawn yet — the winner and backups are never drawn twice.
  remaining() {
    const drawn = new Set(this.results.map((result) => result.participant.number))
    return this.participants.filter((participant) => !drawn.has(participant.number))
  }

  draw() {
    if (this.running || this.results.length > this.backupsValue) return

    const pool = this.remaining()
    if (pool.length === 0) return

    this.running = true
    this.buttonTarget.disabled = true
    this.labelTarget.classList.add("hidden")
    this.drawnAtTarget.classList.add("hidden")
    this.nameTarget.classList.remove("scale-110", "text-amber-300")
    this.commentLinkTarget.classList.add("hidden")
    this.confettiTarget.replaceChildren()

    const winner = pool[this.randomIndex(pool.length)]
    const start = performance.now()
    let lastTick = 0

    const tick = (now) => {
      const progress = Math.min((now - start) / this.durationValue, 1)
      // Ease-out: fast cycling at the beginning, slowing down towards the end.
      const interval = 40 + 560 * Math.pow(progress, 3)

      if (now - lastTick >= interval) {
        lastTick = now
        this.show(pool[this.randomIndex(pool.length)])
      }

      if (progress < 1) {
        requestAnimationFrame(tick)
      } else {
        this.reveal(winner)
      }
    }

    requestAnimationFrame(tick)
  }

  show(participant) {
    const info = PLATFORMS[participant.platform]

    this.numberTarget.textContent = `č. ${participant.number} z ${this.participants.length}`
    this.nameTarget.textContent = participant.name
    this.platformTarget.textContent = participant.username ? `${info.label} · @${participant.username}` : info.label
    this.platformTarget.className = `rounded-full px-3 py-1 text-sm font-medium ${info.chip}`
  }

  reveal(participant) {
    const result = { participant, drawnAt: new Date(), place: this.results.length }
    this.results.push(result)

    this.show(participant)
    this.nameTarget.classList.add("scale-110", "text-amber-300")
    this.labelTarget.textContent = this.placeLabel(result.place, true)
    this.labelTarget.classList.remove("hidden")
    this.drawnAtTarget.textContent = `Vyžrebované ${this.formatDate(result.drawnAt)} o ${this.formatTime(result.drawnAt)}`
    this.drawnAtTarget.classList.remove("hidden")
    this.commentLinkTarget.href = participant.url
    this.commentLinkTarget.textContent = `Zobraziť komentár na ${PLATFORMS[participant.platform].locative}`
    this.commentLinkTarget.classList.remove("hidden")
    this.renderResults()
    this.highlightRows()
    if (result.place === 0) this.launchConfetti()

    this.running = false
    this.updateButton()
  }

  placeLabel(place, celebrate = false) {
    if (place === 0) return celebrate ? "🎉 Výherca" : "Výherca"
    return `${place}. náhradník`
  }

  // The winner is final: after the first draw the button only offers backups,
  // and once all of them are drawn it stays locked.
  updateButton() {
    if (this.results.length > this.backupsValue) {
      this.buttonTarget.textContent = "Žrebovanie ukončené"
      this.buttonTarget.disabled = true
    } else {
      this.buttonTarget.textContent = "Žrebovať náhradníka"
      this.buttonTarget.disabled = false
    }
  }

  renderResults() {
    const rows = this.results.map((result) => {
      const { participant, drawnAt, place } = result
      const info = PLATFORMS[participant.platform]
      const isWinner = place === 0

      const row = document.createElement("li")
      row.className = `flex flex-wrap items-baseline gap-x-4 gap-y-1 px-5 py-3 ${isWinner ? "text-amber-300" : "text-slate-200"}`
      row.dataset.number = participant.number

      const label = document.createElement("span")
      label.className = `w-28 shrink-0 text-xs font-semibold uppercase tracking-wider ${isWinner ? "" : "text-slate-500"}`
      label.textContent = this.placeLabel(place)

      const number = document.createElement("span")
      number.className = "w-16 shrink-0 font-mono tabular-nums"
      number.textContent = `č. ${participant.number}`

      const name = document.createElement("span")
      name.className = "font-semibold"
      name.textContent = participant.name

      const platform = document.createElement("span")
      platform.className = `rounded-full px-2 py-0.5 text-xs font-medium ${info.chip}`
      platform.textContent = info.label

      const time = document.createElement("span")
      time.className = "ml-auto font-mono text-sm tabular-nums text-slate-500"
      time.textContent = this.formatTime(drawnAt)

      row.append(label, number, name, ...this.usernameElements(participant), platform, time)
      return row
    })

    this.resultsTarget.replaceChildren(...rows)
    this.resultsTarget.classList.remove("hidden")
  }

  highlightRows() {
    const places = new Map(this.results.map((result) => [result.participant.number, result.place]))

    for (const row of this.listTarget.children) {
      const place = places.get(Number(row.dataset.number))
      const isWinner = place === 0
      const isBackup = place !== undefined && !isWinner
      row.classList.toggle("bg-amber-400/15", isWinner)
      row.classList.toggle("text-amber-300", isWinner)
      row.classList.toggle("bg-white/10", isBackup)
      row.classList.toggle("font-semibold", place !== undefined)
    }
  }

  formatTime(date) {
    return date.toLocaleTimeString("sk-SK", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
  }

  formatDate(date) {
    return date.toLocaleDateString("sk-SK", { day: "numeric", month: "numeric", year: "numeric" })
  }

  randomIndex(max) {
    const buffer = new Uint32Array(1)
    const limit = Math.floor(0x100000000 / max) * max

    let value
    do {
      crypto.getRandomValues(buffer)
      value = buffer[0]
    } while (value >= limit)

    return value % max
  }

  launchConfetti() {
    const colors = ["#fbbf24", "#f472b6", "#34d399", "#60a5fa", "#f87171", "#ffffff"]
    const pieces = Array.from({ length: 120 }, (_, index) => {
      const piece = document.createElement("span")
      piece.className = "contest-confetti"
      piece.style.left = `${Math.random() * 100}%`
      piece.style.backgroundColor = colors[index % colors.length]
      piece.style.animationDelay = `${Math.random() * 1.5}s`
      piece.style.animationDuration = `${2.5 + Math.random() * 2}s`
      piece.style.transform = `rotate(${Math.random() * 360}deg)`
      return piece
    })

    this.confettiTarget.replaceChildren(...pieces)
  }
}
